import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { socketIOService } from '../services/websocket';
import { useUser } from './UserContext';
import { toast } from 'react-toastify';
import { getMissedNotifications, markNotificationAsRead, markAllNotificationsAsRead, deleteNotificationFromDb, deleteAllNotificationsFromDb } from '../api/apiHelper';
import { pushNotificationService } from '../services/pushNotificationService';
import { useMissedNotificationsPolling } from '../hooks/useMissedNotificationsPolling';

interface Notification {
  id: string;
  dbId?: number; // Database ID for notifications that come from the backend
  type: 'admin_appointment' | 'verification_status' | 'spare_part_requested' | 'spare_part_available' | 'spare_part_delivered' | 'appointment_approval'| 'appointment_removal' | 'general';
  title: string;
  message: string;
  timestamp: string;
  created_at?: string; // Backend returns this field for database notifications
  read: boolean;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  deleteNotification: (id: string) => Promise<void>;
  clearAllNotifications: () => void;
  addNotification: (notification: Omit<Notification, 'id' | 'read'>) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);
const translateSlotTime = (slotTime: string) => {
  switch (slotTime) {
    case 'morning': return 'Manhã';
    case 'afternoon': return 'Tarde';
    case 'night': return 'Noite';
    default: return slotTime;
  }
};
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// Safe hook that returns null instead of throwing an error
export const useNotificationsSafe = () => {
  const context = useContext(NotificationContext);
  return context || null;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const { user } = useUser();

  // Load notifications from localStorage on mount
  useEffect(() => {
    if (!user?.id) {
      setNotifications([]);
      return;
    }

    const storageKey = `notifications_${user.id}`;
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        const parsedNotifications = JSON.parse(stored);
        setNotifications(parsedNotifications);
      } catch (error) {
        console.error('Error loading notifications from localStorage:', error);
        setNotifications([]);
      }
    } else {
      setNotifications([]);
    }
  }, [user?.id]);

  // Save notifications to localStorage whenever they change
  useEffect(() => {
    if (user?.id && notifications.length > 0) {
      const storageKey = `notifications_${user.id}`;
      localStorage.setItem(storageKey, JSON.stringify(notifications));
    }
  }, [notifications, user?.id]);

  const sendPushNotification = useCallback(async (notification: Notification) => {
    try {
      if (document.visibilityState === 'hidden') {
        const isSubscribed = await pushNotificationService.isSubscribed();
        if (isSubscribed) {
          console.log('User is subscribed to push notifications, notification should be sent via backend');
        }
      }
    } catch (error) {
      console.error('Error checking push notification status:', error);
    }
  }, []);

  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'read'>) => {
    const newNotification: Notification = {
      ...notification,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      read: false,
    };

    setNotifications(prev => [newNotification, ...prev]);

    sendPushNotification(newNotification);

    return newNotification;
  }, [sendPushNotification]);

  const markAsRead = async (id: string) => {
    // Find the notification to check if it has a database ID
    const notification = notifications.find(notif => notif.id === id);
    
    // Update local state immediately for better UX
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === id ? { ...notif, read: true } : notif
      )
    );

    // If the notification has a database ID, also mark it as read in the backend
    if (notification?.dbId) {
      try {
        console.log(`📤 Marking notification ${notification.dbId} as read in database`);
        await markNotificationAsRead(notification.dbId);
        console.log(`✅ Successfully marked notification ${notification.dbId} as read in database`);
      } catch (error) {
        console.error('Failed to mark notification as read in database:', error);
        // Note: We don't revert the local state change as the user experience
        // should still work even if the backend call fails
      }
    }
  };

  const markAllAsRead = async () => {
    // Update local state immediately for better UX
    setNotifications(prev => 
      prev.map(notif => ({ ...notif, read: true }))
    );

    // Also mark all notifications as read in the backend
    try {
      console.log(`📤 Marking all notifications as read in database`);
      await markAllNotificationsAsRead();
      console.log(`✅ Successfully marked all notifications as read in database`);
    } catch (error) {
      console.error('Failed to mark all notifications as read in database:', error);
    }
  };

  const deleteNotification = async (id: string) => {
    const notif = notifications.find(n => n.id === id);
    if (notif?.dbId) {
      try {
        await deleteNotificationFromDb(notif.dbId);
      } catch (e) {
        console.error('Failed to delete notification from db', e);
      }
    }
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const clearAllNotifications = async () => {
    if (user?.id) {
      try {
        await deleteAllNotificationsFromDb();
      } catch (error) {
        console.error('Failed to delete all notifications from database:', error);
      }
      const storageKey = `notifications_${user.id}`;
      localStorage.removeItem(storageKey);
    }
    setNotifications([]);
  };

  const handlePolledNotifications = useCallback((missedNotifications: any[]) => {
    if (!missedNotifications?.length) return;

    const newNotifications = missedNotifications.filter((notif: any) => {
      const alreadyExists = notifications.some(existing => existing.dbId === notif.id);
      return !alreadyExists;
    });

    if (newNotifications.length === 0) {
      return;
    }

    newNotifications.forEach((notif: any) => {
      addNotification({
        dbId: notif.id,
        type: notif.type,
        title: notif.title,
        message: notif.message,
        timestamp: notif.created_at || notif.timestamp,
      });
    });

    toast.info(`📩 ${newNotifications.length} nova(s) notificação(ões)`, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  }, [addNotification, notifications]);

  const ENABLE_MISSED_NOTIFICATIONS = true;
  useMissedNotificationsPolling({
    userId: user?.id ?? null,
    enabled: ENABLE_MISSED_NOTIFICATIONS && !!user?.id,
    onNotificationsReceived: handlePolledNotifications,
    pollingInterval: 3600000,
  });

  // Fetch missed notifications when user logs in
  useEffect(() => {
    if (!user?.id || !user?.lastLoginTime) {
      return;
    }

    // Feature flag - disable missed notifications until backend endpoint is ready
    const ENABLE_MISSED_NOTIFICATIONS = true;
    if (!ENABLE_MISSED_NOTIFICATIONS) {
      return;
    }

    // Capture user data in local variables for TypeScript safety in async context
    const userId = user.id;
    const currentLoginTime = user.lastLoginTime;

    // Check if we've already fetched for THIS specific login session
    // We store the login time we last fetched for
    const lastFetchedForLoginKey = `missedNotifsFetchedFor_${userId}`;
    const lastFetchedForLogin = localStorage.getItem(lastFetchedForLoginKey);

    if (lastFetchedForLogin === currentLoginTime) {
      return;
    }

    const fetchMissedNotifications = async () => {
      try {
        // Get the last login time from localStorage
        // This represents when the user PREVIOUSLY logged in (not current session)
        const previousLoginKey = `previousLoginTime_${userId}`;
        const previousLoginTime = localStorage.getItem(previousLoginKey);

        // If no previous login time, fetch from 24 hours ago; otherwise use previous login time
        const fetchSince = previousLoginTime || new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

        const missedNotifications = await getMissedNotifications(fetchSince);

        // Add missed notifications to the context
        if (missedNotifications && Array.isArray(missedNotifications) && missedNotifications.length > 0) {
          missedNotifications.forEach((notif: any) => {
            addNotification({
              dbId: notif.id,
              type: notif.type,
              title: notif.title,
              message: notif.message,
              timestamp: notif.created_at || notif.timestamp,
            });
          });

          // Show a summary toast
          toast.info(`📩 Você tem ${missedNotifications.length} notificação(ões) perdida(s)`, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }

        // Mark that we've fetched for this specific login session
        localStorage.setItem(lastFetchedForLoginKey, currentLoginTime);
        // Store the current login time as the previous login time for the next session
        localStorage.setItem(previousLoginKey, currentLoginTime);
      } catch (error: any) {
        localStorage.setItem(lastFetchedForLoginKey, currentLoginTime);
      }
    };

    // Add a small delay to ensure user context is fully loaded
    const timer = setTimeout(fetchMissedNotifications, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, [user?.id, user?.lastLoginTime, addNotification]);

  useEffect(() => {
    if (!user?.id) return;

    // Listen for admin appointment notifications
    const unsubAdminAppointment = socketIOService.subscribeToEvent('admin_appointment_created', (data: any) => {
      console.log('Received admin appointment notification:', data);
      
      // Don't show notification to admin users since they are the ones performing these actions
      if (user?.role === 'admin') {
        return;
      }
      
      // Don't show toast if the technician created the appointment themselves
      // Check if the assigned technician (who receives the notification) is the same as the current user
      if (data.technicianId && user?.id && data.technicianId === user.id && data.createdBy === user.id) {
        return;
      }
      
      const notification = addNotification({
        type: 'admin_appointment',
        title: 'Novo Agendamento',
        message: data.message || `Agendamento criado pelo administrador para ${data.scheduledDate}`,
        timestamp: data.timestamp || new Date().toISOString(),
      });

      // Show toast notification
      toast.info(`🗓️ ${notification.title}: ${notification.message}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    });

    // Listen for admin appointment removal notifications
    const unsubAdminRemoval = socketIOService.subscribeToEvent('appointment_removed', (data: any) => {
      const notification = addNotification({
        type: 'appointment_removal',
        title: 'Agendamento removido',
        message: data.message || `Agendamento removido`,
        timestamp: data.timestamp || new Date().toISOString(),
      });

      // Show toast notification
      toast.info(`🗓️ ${notification.title}: ${notification.message}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    });

    // Listen for verification status updates
    const unsubVerification = socketIOService.subscribeToEvent('verification_status_updated', (data: any) => {
      console.log('Received verification status notification:', data);
      
      const notification = addNotification({
        type: 'verification_status',
        title: data.status === 'approved' ? 'Documentos Aprovados' : 'Documentos Rejeitados',
        message: data.message,
        timestamp: new Date().toISOString(),
      });

      // Show toast notification
      const toastType = data.status === 'approved' ? 'success' : 'error';
      toast[toastType](`${data.status === 'approved' ? '✅' : '❌'} ${notification.message}`, {
        position: "top-right",
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    });

    // Listen for appointment cancellation toast notifications
    const unsubCancelledToast = socketIOService.subscribeToEvent('appointment_cancelled_toast', (data: any) => {
      console.log('Received appointment cancelled toast notification:', data);

      // Add to persistent notifications menu
      const notification = addNotification({
        type: 'admin_appointment',
        title: 'Agendamento Cancelado',
        message: data.message || 'Um agendamento foi cancelado',
        timestamp: new Date().toISOString(),
      });

      // Show toast notification immediately
      toast.warning(`❌ ${notification.message}`, {
        position: "top-right",
        autoClose: 7000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    });

    // Listen for calendar updates to show toast for appointment cancellations (admin only)
    const unsubCalendarUpdate = socketIOService.subscribeCalendarUpdate((data: any) => {
      if (data.type === 'appointment_cancelled' && user?.role === 'admin') {
        // console.log('Calendar update: appointment cancelled', data);
      }
    });

    // Listen for spare part requested notifications
    const unsubSparePartRequested = socketIOService.subscribeToEvent('spare_part_requested', (data: any) => {
      addNotification({
        type: 'spare_part_requested',
        title: 'Peça Solicitada',
        message: data.message || `Técnico solicitou peça: ${data.partName}`,
        timestamp: data.timestamp || new Date().toISOString(),
      });
    });

    // Listen for spare part available notifications
    const unsubSparePartAvailable = socketIOService.subscribeToEvent('spare_part_available', (data: any) => {
      console.log('Received spare part available notification:', data);
      
      const notification = addNotification({
        type: 'spare_part_available',
        title: 'Peça Disponível',
        message: data.message || `Peça ${data.partName} está disponível para retirada`,
        timestamp: data.timestamp || new Date().toISOString(),
      });

      // Show toast notification
      toast.success(`✅ ${notification.title}: ${notification.message}`, {
        position: "top-right",
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    });

    // Listen for spare part pickup scheduled notifications
    const unsubSparePartPickupScheduled = socketIOService.subscribeToEvent('spare_part_delivered', (data: any) => {
      console.log('Received spare part pickup scheduled notification:', data);
      
      const notification = addNotification({
        type: 'spare_part_delivered',
        title: 'Peça Retirada',
        message: data.message || `Peça retirada ${data.partName}`,
        timestamp: data.timestamp || new Date().toISOString(),
      });

      // Show toast notification
      toast.success(`📅 ${notification.title}: ${notification.message}`, {
        position: "top-right",
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    });

    // Listen for appointment approval request notifications (admin only)
    const unsubAppointmentApproval = socketIOService.subscribeToEvent('appointment_approval_request', (data: any) => {
      console.log('Received appointment approval request notification:', data);

      // Only add notification for admin users
      if (user?.role === 'admin') {
        addNotification({
          type: 'appointment_approval',
          title: 'Aprovação de Agendamento',
          message: `${data.technician_name} solicitou aprovação para um agendamento`,
          timestamp: data.timestamp || new Date().toISOString(),
        });

        // Show toast notification (moved from App.tsx)
        toast.info(`${data.technician_name} solicitou aprovação para um agendamento. Vá para /platform/approvals para revisar.`, {
          position: "top-right",
          autoClose: 8000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    });

    // Listen for appointment updated notifications (when client changes appointment details)
    const unsubAppointmentUpdated = socketIOService.subscribeToEvent('appointment_updated', (data: any) => {
      console.log('Received appointment updated notification:', data);

      const notification = addNotification({
        type: 'general',
        title: 'Agendamento Atualizado',
        message: data.message || `${data.clientName || 'Cliente'} atualizou os detalhes do agendamento (FE3)`,
        timestamp: data.timestamp || new Date().toISOString(),
      });

      // Show toast notification
      toast.info(data.message || `${data.clientName || 'Cliente'} atualizou os detalhes do agendamento (FE4)`, {
        position: "top-right",
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      // Trigger polling for technicians (same pattern as new_appointment)
      if (user?.role === 'technician') {
        window.dispatchEvent(new CustomEvent('appointmentUpdatedForTechnician'));
      }
    });

    // Listen for admin appointment updated notifications
    const unsubAdminAppointmentUpdated = socketIOService.subscribeToEvent('admin_appointment_updated', (data: any) => {
      console.log('🔔 FRONTEND - Received admin_appointment_updated event:', {
        data,
        userId: user?.id,
        userRole: user?.role,
        timestamp: new Date().toISOString(),
      });

      // Don't show notification to admin users since they are the ones performing these actions
      if (user?.role === 'admin') {
        console.log('⚠️ FRONTEND - Suppressing admin appointment updated notification for admin user');
        return;
      }

      console.log('✅ FRONTEND - Adding notification to context and showing toast');
      const notification = addNotification({
        type: 'admin_appointment',
        title: 'Agendamento Atualizado',
        message: data.message || 'Administrador alterou seu agendamento',
        timestamp: data.timestamp || new Date().toISOString(),
      });

      // Show toast notification
      toast.info(`📅 ${notification.message}`, {
        position: "top-right",
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      console.log('✅ FRONTEND - Toast notification displayed');
    });

    // Listen for deleted appointment notifications (receives just appointment ID)
    const unsubDeletedAppointment = socketIOService.subscribeToEvent('deleted_appointment', (appointmentId: number) => {
      console.log('Received deleted appointment notification for ID:', appointmentId);

      const notification = addNotification({
        type: 'admin_appointment',
        title: 'Agendamento Cancelado',
        message: `Seu agendamento #${appointmentId} foi cancelado`,
        timestamp: new Date().toISOString(),
      });

      // Show toast notification
      toast.warning(`❌ ${notification.message}`, {
        position: "top-right",
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    });

    // Listen for new appointment notifications (moved from UserAgenda.tsx)
    const unsubNewAppointment = socketIOService.subscribeNewAppointment((data: any) => {
      // Skip notification if current user is the creator
      const creatorId = data.user_id || data.creator?.id;
      if (creatorId && String(creatorId) === String(user?.id)) {
        console.log('🔔 NotificationContext: Skipping notification - current user is the creator');
        return;
      }

      // For technicians, trigger the auction UI but don't create a notification entry
      if (user?.role === 'technician') {
        console.log('🔔 NotificationContext: Triggering auction UI for technician (no notification entry)');
        window.dispatchEvent(new CustomEvent('newAppointmentForTechnician'));
        return;
      }

      // For admins and customer users, create a notification entry
      console.log('🔔 NotificationContext: Adding notification for new appointment');
      addNotification({
        type: 'admin_appointment',
        title: 'Novo Agendamento',
        message: `(FE1) Novo agendamento para ${data.scheduled_date} ${translateSlotTime(data.scheduled_time)}`,
        timestamp: new Date().toISOString(),
      });
    });

    // Listen for appointment cancelled notifications (moved from AdminAgenda.tsx)
    const unsubAppointmentCancelled = socketIOService.subscribeToEvent('appointment_cancelled', (data: any) => {
      console.log('Received appointment_cancelled notification:', data);

      // Only show notification to admins
      if (user?.role === 'admin') {
        addNotification({
          type: 'admin_appointment',
          title: 'Agendamento Cancelado',
          message: `Agendamento cancelado por ${data.technicianName}: ${data.clientName}`,
          timestamp: new Date().toISOString(),
        });
      }
    });

    // Listen for appointment deleted notifications
    const unsubAppointmentDeleted = socketIOService.subscribeToEvent('appointment_deleted', (data: any) => {
      console.log('Received appointment_deleted notification:', data);

      // Only show notification to admins (since it's emitted via emitToAdmins)
      if (user?.role === 'admin') {
        const notification = addNotification({
          type: 'appointment_removal',
          title: 'Agendamento Removido',
          message: data.message || 'Um agendamento foi removido',
          timestamp: new Date().toISOString(),
        });

        // Show toast notification
        toast.warning(`🗑️ ${notification.title}: ${notification.message}`, {
          position: "top-right",
          autoClose: 6000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    });

    // Listen for appointment approved notifications (moved from App.tsx)
    const unsubAppointmentApproved = socketIOService.subscribeToEvent('appointment_approved', (data: any) => {
      console.log('Received appointment_approved notification:', data);

      if (user?.role === 'technician') {
        const approvalMessage = `Seu agendamento para ${data.client_name} foi aprovado!`;

        // Show toast notification
        toast.success(`✅ ${approvalMessage}`, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        // Add to notification menu
        addNotification({
          type: 'admin_appointment',
          title: 'Agendamento Aprovado',
          message: approvalMessage,
          timestamp: new Date().toISOString(),
        });
      } else if (user?.role === 'user-internal' || user?.role === 'user-external') {
        addNotification({
          type: 'admin_appointment',
          title: 'Novo Agendamento',
          message: `(FE2) Novo agendamento para ${data.scheduled_date} ${translateSlotTime(data.scheduled_time)}`,
          timestamp: new Date().toISOString(),
        });
      }
    });

    // Listen for appointment rejected notifications (moved from App.tsx)
    const unsubAppointmentRejected = socketIOService.subscribeToEvent('appointment_rejected', (data: any) => {
      console.log('Received appointment_rejected notification:', data);

      // Only show notification to technicians
      if (user?.role === 'technician') {
        const rejectionMessage = data.admin_notes
          ? `Seu agendamento para ${data.client_name} foi rejeitado. Motivo: ${data.admin_notes}`
          : `Seu agendamento para ${data.client_name} foi rejeitado.`;

        // Show toast notification
        toast.error(`❌ ${rejectionMessage}`, {
          position: "top-right",
          autoClose: 8000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        // Add to notification menu
        addNotification({
          type: 'admin_appointment',
          title: 'Agendamento Rejeitado',
          message: rejectionMessage,
          timestamp: new Date().toISOString(),
        });
      }
    });

    // Listen for profile review needed notifications (moved from App.tsx)
    const unsubProfileReviewNeeded = socketIOService.subscribeToEvent('profile_review_needed', (data: any) => {
      console.log('Received profile_review_needed notification:', data);

      // Only show toast to admins
      if (user?.role === 'admin') {
        toast.info(`${data.userName} submeteu documentos para verificação. Vá para /platform/approvals para revisar.`, {
          position: "top-right",
          autoClose: 8000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          onClick: () => window.open('/platform/approvals', '_blank')
        });
      }
    });

    return () => {
      console.log('🔧 NotificationContext: Cleaning up websocket listeners');
      unsubAdminAppointment();
      unsubAdminRemoval();
      unsubVerification();
      unsubCancelledToast();
      unsubCalendarUpdate();
      unsubSparePartRequested();
      unsubSparePartAvailable();
      unsubSparePartPickupScheduled();
      unsubAppointmentApproval();
      unsubAppointmentUpdated();
      unsubAdminAppointmentUpdated();
      unsubDeletedAppointment();
      unsubNewAppointment();
      unsubAppointmentCancelled();
      unsubAppointmentDeleted();
      unsubAppointmentApproved();
      unsubAppointmentRejected();
      unsubProfileReviewNeeded();
    };
  }, [user?.id, user?.role]);

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <NotificationContext.Provider 
      value={{ 
        notifications, 
        unreadCount, 
        markAsRead, 
        markAllAsRead, 
        deleteNotification,
        clearAllNotifications,
        addNotification 
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};